import { useState } from 'react'

import { Box, Button, Section, Tag, Text } from '@factorialco/deprecated-design-system'

import {
  BookkeepersDocument,
  BulkUpdateBookkeeperDocument,
  BulkUpdateBookkeeperMutation,
} from 'generated/resources/graphql'
import type { BookkeepersQuery } from 'generated/resources/graphql'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Bookkeeper =
  BookkeepersQuery['bookkeepersManagement']['bookkeepers'][number]

interface BookkeepersListProps {
  bookkeepers: Bookkeeper[]
  removeInactiveBookkeeper: (accessId: string) => void
}

const BookkeepersList = ({ bookkeepers, removeInactiveBookkeeper }: BookkeepersListProps) => {
  const bookkeepersWithAccess = bookkeepers.filter((b) => b.access != null)

  return bookkeepersWithAccess.map((bookkeeper, index, collection) => {
    const lastItemIndex = collection.length - 1
    const isLast = index === lastItemIndex

    return (
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        border={
          !isLast && {
            bottom: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }
        }
        padding="s16"
        key={bookkeeper.id}
      >
        <Text.Normal size="300" color="grey1000" weight="normal">
          {bookkeeper?.access?.fullName}
        </Text.Normal>

        {bookkeeper?.access?.isDeactivated && (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box>
              <Tag size="small" leftItem="circle" showCircle type={Tag.Types.grey}>
                {i18n.t(
                  'app_store_v2.apps.payroll_bundle.settings.professional_settings.status.inactive'
                )}
              </Tag>
            </Box>

            <Button
              type="secondary"
              onClick={() => {
                const accessId = bookkeeper.access?.id
                if (accessId) removeInactiveBookkeeper(accessId)
              }}
              iconName={'delete'}
            ></Button>
          </Box>
        )}
      </Box>
    )
  })
}

const ProfessionalSettingsContent = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading, error } = useQuery(BookkeepersDocument, {
    variables: {
      companyIds: [companyId],
    },
  })

  const bookkeepers = data?.bookkeepersManagement.bookkeepers ?? []
  const bookkeeperAccessIds = bookkeepers
    .map((bookkeeper) => bookkeeper?.access?.id)
    .filter((id): id is string => Boolean(id))

  const [bookkeepersModal, setBookkeepersModal] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isSubmitting, setSubmitting] = useState(false)

  const [bulkUpdateBookkeepers] = useMutation(BulkUpdateBookkeeperDocument, {
    extractorFn: (mutationData: BulkUpdateBookkeeperMutation) =>
      mutationData.bookkeepersManagementMutations.bulkUpdateBookkeepersmanagementBookkeeper,
  })

  const updateBookkeepers = async (newBookkeepersIds: string[]) => {
    setSubmitting(true)
    try {
      const handled = handleResult(
        await bulkUpdateBookkeepers({
          variables: {
            bookkeeperAccessIds: newBookkeepersIds,
            companyId,
          },
          refetchQueries: [
            {
              query: BookkeepersDocument,
              variables: { companyIds: [companyId] },
            },
          ],
          awaitRefetchQueries: true,
        }),
        (errors) => {
          const errorMessage = Object.values(errors).join(', ')
          app.setFlash({
            type: 'wrong',
            message: errorMessage || i18n.t('common.save_error'),
          })
        }
      )

      if (handled.success) {
        setBookkeepersModal(false)
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const removeInactiveBookkeeper = (accessId: string) => {
    const activeBookkeepers = bookkeeperAccessIds.filter((id) => id !== accessId)
    updateBookkeepers(activeBookkeepers)
  }

  const submitLabel = selectedIds.length
    ? i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit')
    : i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit_nobookkeeper')

  if (loading) return <Loading />
  if (error) {
    return (
      <Text.Normal size="300" color="grey800" weight="medium">
        {i18n.t('common.save_error')}
      </Text.Normal>
    )
  }

  return (
    <Box gap="s24">
      <Box
        borderRadius={{
          all: 'abs016',
        }}
        border={
          bookkeepers.length
            ? {
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }
            : {}
        }
      >
        <Box>
          {bookkeepers.length ? (
            <BookkeepersList
              bookkeepers={bookkeepers}
              removeInactiveBookkeeper={removeInactiveBookkeeper}
            />
          ) : (
            <Text.Normal size="300" color="grey800" weight="medium">
              {i18n.t('app_store_v2.apps.payroll_bundle.settings.no_bookkeeper_selected')}
            </Text.Normal>
          )}
        </Box>
        {bookkeepersModal && (
          <Modal
            illustration="user-tie"
            title={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.title')}
            description={i18n.t(
              'app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.modal_description'
            )}
            onClose={() => setBookkeepersModal(false)}
            onSubmit={async () => {
              await updateBookkeepers(selectedIds)
            }}
            footer={
              <>
                <Button
                  type="secondary"
                  disabled={isSubmitting}
                  onClick={() => {
                    setBookkeepersModal(false)
                  }}
                >
                  {i18n.t('common.cancel')}
                </Button>
                <Button disabled={isSubmitting} submit>
                  {submitLabel}
                </Button>
              </>
            }
          >
            <Box gap="s24">
              <EmployeeSelectorV2
                multiple
                useAccessId
                label={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.label')}
                value={selectedIds}
                onChange={(state) => {
                  if (state.type === 'manual') {
                    setSelectedIds(state.ids)
                  }
                }}
                forcedFilters={{ accessStatus: ['active'] }}
                options={{ disableSelectAll: true }}
                affiliations={['employee', 'external']}
              />
            </Box>
          </Modal>
        )}
      </Box>

      <Button
        type="secondary"
        onClick={() => {
          setSelectedIds(bookkeeperAccessIds)
          setBookkeepersModal(true)
        }}
        iconName={bookkeepers.length ? 'users' : 'add'}
      >
        {bookkeepers.length
          ? i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.button.manage')
          : i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.button.add')}
      </Button>
    </Box>
  )
}

const ProfessionalSettings = () => (
  <Section
    title={i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.section_title')}
    illustration="briefcase"
    description={i18n.t(
      'app_store_v2.apps.payroll_bundle.settings.professional_settings.section_description'
    )}
  >
    <ProfessionalSettingsContent />
  </Section>
)

export default ProfessionalSettings
